import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { db, COLLECTIONS } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  updateDoc, 
  doc,
  addDoc,
  serverTimestamp,
  writeBatch,
  deleteDoc
} from 'firebase/firestore';
import { 
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Trash2,
  Filter,
  Search,
  Calendar,
  User,
  AlertTriangle,
  Info,
  CheckCircle,
  Clock,
  Settings,
  Volume2,
  VolumeX,
  Smartphone,
  Mail,
  MessageSquare,
  Plus,
  Edit,
  Save,
  X,
  Target,
  Zap,
  Users,
  FileText,
  AlertCircle
} from 'lucide-react';

const Notifications = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { users, departments } = useData();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [typeFilter, setTypeFilter] = useState('all'); // all, task, project, system, reminder
  const [priorityFilter, setPriorityFilter] = useState('all'); // all, high, medium, low
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showCustomRule, setShowCustomRule] = useState(false);
  const [activeTab, setActiveTab] = useState('notifications'); // notifications, settings, rules
  
  // User notification settings
  const [userSettings, setUserSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    reminderFrequency: 'daily', // daily, weekly, custom
    autoMarkRead: false,
    soundEnabled: true,
    taskAssignments: true,
    statusUpdates: true,
    projectUpdates: true,
    systemAlerts: true,
    reminderTime: '09:00',
    weekendReminders: false,
    digestMode: false, // Send summary instead of individual notifications
    quietHours: { start: '22:00', end: '08:00' }
  });

  // Custom notification rules
  const [customRules, setCustomRules] = useState([]);
  const [newRule, setNewRule] = useState({
    name: '',
    trigger: 'task_overdue',
    action: 'email',
    frequency: 'once',
    conditions: {},
    active: true
  });

  // Notification types configuration
  const notificationTypes = {
    'task_assigned': {
      icon: CheckCircle,
      color: 'blue',
      label: 'משימה חדשה',
      description: 'הוקצתה לך משימה חדשה'
    },
    'task_due_soon': {
      icon: Clock,
      color: 'orange',
      label: 'תזכורת דדליין',
      description: 'משימה מתקרבת לדדליין'
    },
    'task_overdue': {
      icon: AlertTriangle,
      color: 'red',
      label: 'משימה באיחור',
      description: 'משימה עברה את הדדליין'
    },
    'status_update_required': {
      icon: MessageSquare,
      color: 'purple',
      label: 'נדרש עדכון סטטוס',
      description: 'זמן לעדכן סטטוס משימה'
    },
    'project_milestone': {
      icon: Target,
      color: 'green',
      label: 'אבן דרך בפרויקט',
      description: 'הושגה אבן דרך חשובה'
    },
    'acknowledgment_reminder': {
      icon: Bell,
      color: 'yellow',
      label: 'תזכורת אישור',
      description: 'יש לאשר קבלת משימה'
    },
    'capacity_warning': {
      icon: AlertCircle,
      color: 'red',
      label: 'אזהרת קיבולת',
      description: 'קיבולת העבודה השבועית חרגה'
    },
    'team_update': {
      icon: Users,
      color: 'blue',
      label: 'עדכון צוות',
      description: 'עדכון חשוב לצוות'
    },
    'system_maintenance': {
      icon: Settings,
      color: 'gray',
      label: 'תחזוקת מערכת',
      description: 'הודעת תחזוקה מתוכננת'
    }
  };

  useEffect(() => {
    if (!currentUser?.id) return;

    // Load notifications from Firestore when collection exists
    // const q = query(
    //   collection(db, COLLECTIONS.NOTIFICATIONS),
    //   where('userId', '==', currentUser.id),
    //   orderBy('createdAt', 'desc')
    // );
    // const unsubscribe = onSnapshot(q, (snapshot) => {
    //   const notificationsData = snapshot.docs.map(doc => ({
    //     id: doc.id,
    //     ...doc.data()
    //   })).filter(notification => !notification.deleted);
    //   setNotifications(notificationsData);
    //   setLoading(false);
    // }, (error) => {
    //   console.error('Error loading notifications:', error);
    //   setLoading(false);
    //   setNotifications([]);
    // });

    // Load user settings
    loadUserSettings();
    setLoading(false);

    // return () => unsubscribe();
  }, [currentUser?.id]);

  // Load user notification settings
  const loadUserSettings = async () => {
    try {
      // For now using default settings since the collection might not exist yet
      // In production, you would fetch from Firestore:
      // const settingsDoc = await getDoc(doc(db, COLLECTIONS.USER_SETTINGS, currentUser.id));
      console.log('Loading user settings (using defaults for now)');
    } catch (error) {
      console.error('Error loading user settings:', error);
    }
  };

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread' && notification.isRead) return false;
    if (filter === 'read' && !notification.isRead) return false;
    if (typeFilter !== 'all' && notification.type !== typeFilter) return false;
    if (priorityFilter !== 'all' && notification.priority !== priorityFilter) return false;
    if (searchTerm && !notification.title.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !notification.message.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      // Update local state immediately for better UX
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: true, readAt: new Date() }
            : notification
        )
      );
      
      // In production, also update Firestore:
      // await updateDoc(doc(db, COLLECTIONS.NOTIFICATIONS, notificationId), {
      //   isRead: true,
      //   readAt: serverTimestamp()
      // });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Revert local state if Firestore update fails
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: false, readAt: null }
            : notification
        )
      );
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      // Update local state immediately
      setNotifications(prev => 
        prev.map(notification => ({ 
          ...notification, 
          isRead: true, 
          readAt: new Date() 
        }))
      );
      
      // In production, also update Firestore with batch:
      // const batch = writeBatch(db);
      // const unreadNotifications = notifications.filter(n => !n.isRead);
      // unreadNotifications.forEach(notification => {
      //   const notificationRef = doc(db, COLLECTIONS.NOTIFICATIONS, notification.id);
      //   batch.update(notificationRef, {
      //     isRead: true,
      //     readAt: serverTimestamp()
      //   });
      // });
      // await batch.commit();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      // Update local state immediately
      setNotifications(prev => 
        prev.filter(notification => notification.id !== notificationId)
      );
      
      // In production, also update Firestore:
      // await updateDoc(doc(db, COLLECTIONS.NOTIFICATIONS, notificationId), {
      //   deleted: true,
      //   deletedAt: serverTimestamp()
      // });
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Bulk delete selected notifications
  const deleteSelectedNotifications = async () => {
    try {
      // Update local state immediately
      setNotifications(prev => 
        prev.filter(notification => !selectedNotifications.includes(notification.id))
      );
      setSelectedNotifications([]);
      
      // In production, also update Firestore with batch:
      // const batch = writeBatch(db);
      // selectedNotifications.forEach(notificationId => {
      //   const notificationRef = doc(db, COLLECTIONS.NOTIFICATIONS, notificationId);
      //   batch.update(notificationRef, {
      //     deleted: true,
      //     deletedAt: serverTimestamp()
      //   });
      // });
      // await batch.commit();
    } catch (error) {
      console.error('Error deleting selected notifications:', error);
    }
  };

  // Save user settings
  const saveSettings = async () => {
    try {
      // For now just show success message since collections might not exist yet
      // In production you would save to Firestore:
      // await setDoc(doc(db, COLLECTIONS.USER_SETTINGS, currentUser.id), {
      //   notificationSettings: userSettings,
      //   updatedAt: serverTimestamp()
      // }, { merge: true });
      
      console.log('Settings saved');
      alert('ההגדרות נשמרו בהצלחה!');
      setShowSettings(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('שגיאה בשמירת ההגדרות');
    }
  };

  // Create custom notification rule
  const createCustomRule = async () => {
    try {
      // For now just add to local state since collection might not exist yet
      // In production you would save to Firestore:
      // await addDoc(collection(db, COLLECTIONS.NOTIFICATION_RULES), {
      //   ...newRule,
      //   userId: currentUser.id,
      //   createdAt: serverTimestamp()
      // });
      
      const newRuleWithId = {
        ...newRule,
        id: Date.now().toString(),
        userId: currentUser.id,
        createdAt: new Date()
      };
      
      setCustomRules([...customRules, newRuleWithId]);
      
      setNewRule({
        name: '',
        trigger: 'task_overdue',
        action: 'email',
        frequency: 'once',
        conditions: {},
        active: true
      });
      setShowCustomRule(false);
      alert('הכלל נוצר בהצלחה!');
    } catch (error) {
      console.error('Error creating custom rule:', error);
      alert('שגיאה ביצירת הכלל');
    }
  };

  // Navigate to notification source
  const navigateToSource = (notification) => {
    if (notification.sourceType === 'task' && notification.sourceId) {
      navigate(`/tasks/${notification.sourceId}`);
    } else if (notification.sourceType === 'project' && notification.sourceId) {
      navigate(`/projects/${notification.sourceId}`);
    }
    
    // Mark as read when clicked
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
  };

  // Get time ago string
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const notificationTime = timestamp.toDate();
    const diffMs = now - notificationTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'עכשיו';
    if (diffMins < 60) return `לפני ${diffMins} דקות`;
    if (diffHours < 24) return `לפני ${diffHours} שעות`;
    return `לפני ${diffDays} ימים`;
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    };
    
    const labels = {
      high: 'דחוף',
      medium: 'רגיל',
      low: 'נמוך'
    };

    return (
      <span className={`px-2 py-1 text-xs rounded-full ${colors[priority] || colors.medium}`}>
        {labels[priority] || labels.medium}
      </span>
    );
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const selectedCount = selectedNotifications.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Bell className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">התראות</h1>
            {unreadCount > 0 && (
              <p className="text-sm text-gray-600">{unreadCount} התראות לא נקראו</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <CheckCheck className="h-4 w-4" />
              <span>סמן הכל כנקרא</span>
            </button>
          )}
          
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Settings className="h-4 w-4" />
            <span>הגדרות</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('notifications')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'notifications'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            התראות ({notifications.length})
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            הגדרות
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'rules'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            כללי התראות
          </button>
        </nav>
      </div>

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <>
          {/* Filters and Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-wrap gap-4 mb-4">
              {/* Search */}
              <div className="flex-1 min-w-64">
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="חיפוש התראות..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">כל הסטטוסים</option>
                <option value="unread">לא נקרא</option>
                <option value="read">נקרא</option>
              </select>

              {/* Type Filter */}
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">כל הסוגים</option>
                {Object.entries(notificationTypes).map(([type, config]) => (
                  <option key={type} value={type}>{config.label}</option>
                ))}
              </select>

              {/* Priority Filter */}
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">כל העדיפויות</option>
                <option value="high">דחוף</option>
                <option value="medium">רגיל</option>
                <option value="low">נמוך</option>
              </select>
            </div>

            {/* Bulk Actions */}
            {selectedCount > 0 && (
              <div className="flex items-center justify-between bg-blue-50 p-4 rounded-lg">
                <span className="text-blue-800 font-medium">
                  {selectedCount} התראות נבחרו
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={deleteSelectedNotifications}
                    className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                  >
                    מחק נבחרות
                  </button>
                  <button
                    onClick={() => setSelectedNotifications([])}
                    className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                  >
                    בטל בחירה
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Notifications List */}
          <div className="space-y-3">
            {filteredNotifications.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <BellOff className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">אין התראות</h3>
                <p className="text-gray-600">
                  {filter === 'unread' 
                    ? 'כל ההתראות נקראו'
                    : typeFilter !== 'all'
                    ? `אין התראות מהסוג הנבחר`
                    : 'אין התראות זמינות'
                  }
                </p>
              </div>
            ) : (
              filteredNotifications.map((notification) => {
                const typeConfig = notificationTypes[notification.type] || notificationTypes['task_assigned'];
                const IconComponent = typeConfig.icon;
                
                return (
                  <div
                    key={notification.id}
                    className={`bg-white rounded-lg shadow border-r-4 ${
                      notification.isRead 
                        ? 'border-gray-300 opacity-75' 
                        : `border-${typeConfig.color}-500`
                    } hover:shadow-md transition-all cursor-pointer`}
                    onClick={() => navigateToSource(notification)}
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={selectedNotifications.includes(notification.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (e.target.checked) {
                                setSelectedNotifications([...selectedNotifications, notification.id]);
                              } else {
                                setSelectedNotifications(selectedNotifications.filter(id => id !== notification.id));
                              }
                            }}
                            className="mt-1"
                          />
                          
                          {/* Icon */}
                          <div className={`p-2 rounded-full bg-${typeConfig.color}-100 text-${typeConfig.color}-600`}>
                            <IconComponent className="h-5 w-5" />
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className={`font-medium ${
                                notification.isRead ? 'text-gray-700' : 'text-gray-900'
                              }`}>
                                {notification.title}
                              </h3>
                              {notification.priority && (
                                getPriorityBadge(notification.priority)
                              )}
                            </div>
                            
                            <p className={`text-sm ${
                              notification.isRead ? 'text-gray-500' : 'text-gray-700'
                            } mb-2`}>
                              {notification.message}
                            </p>
                            
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span className="flex items-center space-x-1">
                                <Clock className="h-3 w-3" />
                                <span>{getTimeAgo(notification.createdAt)}</span>
                              </span>
                              
                              <span className={`px-2 py-1 rounded-full bg-${typeConfig.color}-100 text-${typeConfig.color}-700`}>
                                {typeConfig.label}
                              </span>
                              
                              {notification.source && (
                                <span className="flex items-center space-x-1">
                                  <User className="h-3 w-3" />
                                  <span>{notification.source}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center space-x-2">
                          {!notification.isRead && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                              title="סמן כנקרא"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title="מחק התראה"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Action buttons if needed */}
                      {notification.actionUrl && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <a
                            href={notification.actionUrl}
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            {notification.actionText || 'לפרטים נוספים'}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">הגדרות התראות</h2>
            
            {/* Delivery Methods */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">אמצעי משלוח</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Bell className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">התראות במערכת</p>
                      <p className="text-sm text-gray-500">התראות בתוך האפליקציה</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={userSettings.pushNotifications}
                    onChange={(e) => setUserSettings({...userSettings, pushNotifications: e.target.checked})}
                    className="h-4 w-4 text-blue-600"
                  />
                </label>
                
                <label className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">התראות במייל</p>
                      <p className="text-sm text-gray-500">קבלת התראות למייל האישי</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={userSettings.emailNotifications}
                    onChange={(e) => setUserSettings({...userSettings, emailNotifications: e.target.checked})}
                    className="h-4 w-4 text-blue-600"
                  />
                </label>
                
                <label className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Smartphone className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">הודעות SMS</p>
                      <p className="text-sm text-gray-500">התראות דחופות בלבד</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={userSettings.smsNotifications}
                    onChange={(e) => setUserSettings({...userSettings, smsNotifications: e.target.checked})}
                    className="h-4 w-4 text-blue-600"
                  />
                </label>
              </div>
            </div>

            {/* Notification Types */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">סוגי התראות</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between">
                  <span className="text-gray-900">הקצאת משימות חדשות</span>
                  <input
                    type="checkbox"
                    checked={userSettings.taskAssignments}
                    onChange={(e) => setUserSettings({...userSettings, taskAssignments: e.target.checked})}
                    className="h-4 w-4 text-blue-600"
                  />
                </label>
                
                <label className="flex items-center justify-between">
                  <span className="text-gray-900">עדכוני סטטוס</span>
                  <input
                    type="checkbox"
                    checked={userSettings.statusUpdates}
                    onChange={(e) => setUserSettings({...userSettings, statusUpdates: e.target.checked})}
                    className="h-4 w-4 text-blue-600"
                  />
                </label>
                
                <label className="flex items-center justify-between">
                  <span className="text-gray-900">עדכוני פרויקט</span>
                  <input
                    type="checkbox"
                    checked={userSettings.projectUpdates}
                    onChange={(e) => setUserSettings({...userSettings, projectUpdates: e.target.checked})}
                    className="h-4 w-4 text-blue-600"
                  />
                </label>
                
                <label className="flex items-center justify-between">
                  <span className="text-gray-900">התראות מערכת</span>
                  <input
                    type="checkbox"
                    checked={userSettings.systemAlerts}
                    onChange={(e) => setUserSettings({...userSettings, systemAlerts: e.target.checked})}
                    className="h-4 w-4 text-blue-600"
                  />
                </label>
              </div>
            </div>

            {/* Timing Settings */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">הגדרות זמן</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    תדירות תזכורות
                  </label>
                  <select
                    value={userSettings.reminderFrequency}
                    onChange={(e) => setUserSettings({...userSettings, reminderFrequency: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="daily">יומי</option>
                    <option value="weekly">שבועי</option>
                    <option value="custom">מותאם אישית</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    שעת תזכורת יומית
                  </label>
                  <input
                    type="time"
                    value={userSettings.reminderTime}
                    onChange={(e) => setUserSettings({...userSettings, reminderTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="mt-4 space-y-3">
                <label className="flex items-center justify-between">
                  <span className="text-gray-900">תזכורות בסוף השבוע</span>
                  <input
                    type="checkbox"
                    checked={userSettings.weekendReminders}
                    onChange={(e) => setUserSettings({...userSettings, weekendReminders: e.target.checked})}
                    className="h-4 w-4 text-blue-600"
                  />
                </label>
                
                <label className="flex items-center justify-between">
                  <span className="text-gray-900">סימון אוטומטי כנקרא לאחר צפייה</span>
                  <input
                    type="checkbox"
                    checked={userSettings.autoMarkRead}
                    onChange={(e) => setUserSettings({...userSettings, autoMarkRead: e.target.checked})}
                    className="h-4 w-4 text-blue-600"
                  />
                </label>
                
                <label className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-900">צלילי התראה</span>
                    {userSettings.soundEnabled ? <Volume2 className="h-4 w-4 text-green-600" /> : <VolumeX className="h-4 w-4 text-gray-400" />}
                  </div>
                  <input
                    type="checkbox"
                    checked={userSettings.soundEnabled}
                    onChange={(e) => setUserSettings({...userSettings, soundEnabled: e.target.checked})}
                    className="h-4 w-4 text-blue-600"
                  />
                </label>
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={saveSettings}
                className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Save className="h-4 w-4" />
                <span>שמור הגדרות</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rules Tab */}
      {activeTab === 'rules' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">כללי התראות מותאמים אישית</h2>
            <button
              onClick={() => setShowCustomRule(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              <span>כלל חדש</span>
            </button>
          </div>

          {customRules.length === 0 ? (
            <div className="text-center py-12">
              <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">אין כללים מותאמים אישית</h3>
              <p className="text-gray-600 mb-4">צור כללים מותאמים אישית לקבלת התראות חכמות</p>
              <button
                onClick={() => setShowCustomRule(true)}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                צור כלל ראשון
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {customRules.map((rule) => (
                <div key={rule.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900">{rule.name}</h3>
                    <p className="text-sm text-gray-600">
                      {rule.trigger} → {rule.action} ({rule.frequency})
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={rule.active}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                    <button className="p-2 text-gray-400 hover:text-blue-600">
                      <Edit className="h-4 w-4" />
                    </button>
                    <button className="p-2 text-gray-400 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Custom Rule Modal */}
      {showCustomRule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">כלל התראה חדש</h3>
                <button
                  onClick={() => setShowCustomRule(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">שם הכלל</label>
                  <input
                    type="text"
                    value={newRule.name}
                    onChange={(e) => setNewRule({...newRule, name: e.target.value})}
                    placeholder="למשל: התראה על משימות באיחור"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">מתי להפעיל</label>
                    <select
                      value={newRule.trigger}
                      onChange={(e) => setNewRule({...newRule, trigger: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="task_overdue">משימה באיחור</option>
                      <option value="task_due_soon">משימה מתקרבת לדדליין</option>
                      <option value="status_not_updated">סטטוס לא עודכן</option>
                      <option value="capacity_exceeded">חריגה מקיבולת</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">איך להתריע</label>
                    <select
                      value={newRule.action}
                      onChange={(e) => setNewRule({...newRule, action: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="email">מייל</option>
                      <option value="sms">SMS</option>
                      <option value="push">התראה במערכת</option>
                      <option value="all">כל האמצעים</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">תדירות</label>
                    <select
                      value={newRule.frequency}
                      onChange={(e) => setNewRule({...newRule, frequency: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="once">פעם אחת</option>
                      <option value="daily">יומי</option>
                      <option value="hourly">כל שעה</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCustomRule(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  בטל
                </button>
                <button
                  onClick={createCustomRule}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  צור כלל
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;